import { css } from '@emotion/css'
import { Select } from '@mantine/core'
import { PanelDetailsContext } from 'App/Map/PanelDetails/PanelDetails'
import type { TLngLat } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { useContext, useMemo } from 'react'
import { colors } from 'settings/colors'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { dropRepeatedSelectValues } from 'utils/dropRepeatedSelectValues'
import type { TSelectItemWithExternalVendors } from 'utils/makeExternalVendorSelectProps'
import { makeExternalVendorSelectProps } from 'utils/makeExternalVendorSelectProps'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { getAllPointsForPrePostFilter } from 'utils/setSelectedFieldAsset/_utils/getArgsForAnchorCoordinates'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { sortByKey } from 'utils/sortByKey'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'

type SelectDataGroup = { group: string; items: TSelectItemWithExternalVendors[] }

export const DropdownSelectorPrePostFilter = () => {
  const { selectedPrePostFilterPoint } = selectedFieldAssetsStore.useSelector((s) => ({
    selectedPrePostFilterPoint: s.prePostFilterPoint,
  }))

  const { containerWidth } = useContext(PanelDetailsContext)
  const selectClickToggle = useSelectClickToggle()

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const data = useMemo<SelectDataGroup[]>(() => {
    const groups = Object.values(properties ?? {})
      .sort(sortByKey('propertyName'))
      .map((p) => ({
        group: p.propertyName,
        items: getAllPointsForPrePostFilter(Object.values(p.points ?? {}))
          .sort(sortByKey('name'))
          .map((point) => ({
            value: point.lngLat,
            label: point.name,
            externalVendorNames: point.externalVendorNames,
          })),
      }))

    return dropRepeatedSelectValues(groups)
  }, [selectedPrePostFilterPoint, properties])

  if (!data.length) return null

  const { selectProps, inputPaddingRight } = makeExternalVendorSelectProps({
    data,
    value: selectedPrePostFilterPoint,
  })

  return (
    // can prevent the section from collapsing by passing stopPropagation
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        {...selectClickToggle}
        className={css({
          width: containerWidth < 600 ? 200 : Math.min(280, Math.round(containerWidth / 3)),
          border: `1px solid ${colors.grey500}`,
          color: colors.grey800,
          borderRadius: 3,
        })}
        clearable={false}
        data={data}
        {...selectProps}
        filter={mantineSelectSearchFilterProp}
        onChange={(newSelectedPrePostFilterPoint: string | null) => {
          if (newSelectedPrePostFilterPoint) {
            setSelectedFieldAsset({
              prePostFilterPoint: newSelectedPrePostFilterPoint as TLngLat,
            })
          }
        }}
        placeholder={translate.phrases.banyanApp('Select a filter station')}
        searchable
        size="xs"
        spellCheck="false"
        styles={{
          ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
          dropdown: { marginTop: -6, fontWeight: 'normal' },
          input: {
            ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input,
            paddingRight: inputPaddingRight,
          },
        }}
        value={selectedPrePostFilterPoint}
        comboboxProps={{
          withinPortal: true,
          keepMounted: false,
          zIndex: hubConnectorSettings.zIndexForDropdownSelectorInStackemCharts,
        }}
      />
    </div>
  )
}
